import type { Item } from '@hugo/core'
import { z } from 'zod'

/**
 * One list of everything the user is responsible for (decision 96). Whether someone waits on an
 * item is a property of it, not a list: "I owe" (someone waits on the user) and "Waiting on" (the
 * user waits on someone) are filters of the list and markers on rows and cards.
 */

export const listFilters = ['all', 'owe', 'waiting'] as const
export type ListFilter = (typeof listFilters)[number]

/** The list's filter in the address (`/tasks?filter=owe`); anything else shows everything. */
export const listFilterSchema = z.enum(listFilters).catch('all').default('all')

export const FILTER_LABELS: Record<ListFilter, string> = {
  all: 'All',
  owe: 'I owe',
  waiting: 'Waiting on',
}

export function matchesFilter(item: Pick<Item, 'kind'>, filter: ListFilter): boolean {
  return filter === 'all' || item.kind === filter
}

export function filterCounts(items: readonly Pick<Item, 'kind'>[]): Record<ListFilter, number> {
  return {
    all: items.length,
    owe: items.filter((item) => item.kind === 'owe').length,
    waiting: items.filter((item) => item.kind === 'waiting').length,
  }
}

/**
 * Who waits, on a row or a card: "I owe Nina Graf", "Waiting on Lea Frei" ("someone" without a
 * person); a plain task shows its person, if any.
 */
export function markerText(item: Pick<Item, 'kind' | 'counterpart'>): string | null {
  const person = item.counterpart?.name ?? null
  if (item.kind === 'owe') return `I owe ${person ?? 'someone'}`
  if (item.kind === 'waiting') return `Waiting on ${person ?? 'someone'}`
  return person
}

export type WaitingKind = 'task' | 'owe' | 'waiting'

/** The "Waiting" property of an item, as the details sheet offers it. */
export const WAITING_CHOICES: readonly { kind: WaitingKind; label: string; hint: string }[] = [
  {
    kind: 'task',
    label: 'No one',
    hint: 'Nobody waits on it: a plain task. hugo plans it once it has a date or a day on the board.',
  },
  {
    kind: 'owe',
    label: 'On me',
    hint: "Someone waits on you (I owe): hugo plans it and drafts a note to them when it's going to slip.",
  },
  {
    kind: 'waiting',
    label: 'On someone',
    hint: 'You wait on someone (waiting on): it takes none of your time, and hugo drafts a reminder when the follow-up date comes.',
  },
]

/** The toast after changing who waits: "Waiting: on me". */
export function waitingDone(kind: WaitingKind): string {
  const choice = WAITING_CHOICES.find((c) => c.kind === kind)
  return `Waiting: ${(choice?.label ?? '').toLowerCase()}`
}
